import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PartyPopper, Loader2, Save, RotateCcw, Send } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WelcomeMessageEditor = ({ token, onAuthError }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/welcome-message`, auth)
      .then((r) => setTpl(r.data))
      .catch((e) => { if (e?.response?.status === 401) onAuthError?.(); });
    // eslint-disable-next-line
  }, [token]);

  if (!tpl) return null;

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/settings/welcome-message`,
        { enabled: tpl.enabled, email_subject: tpl.email_subject, body: tpl.body, ...patch }, auth);
      setTpl({ ...tpl, ...data });
      toast.success("Welcome message saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/settings/welcome-message/test`,
        { enabled: tpl.enabled, email_subject: tpl.email_subject, body: tpl.body }, auth);
      toast.success(`Test sent — SMS: ${data.sms} · Email: ${data.email}`, { duration: 8000 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send test."); }
    finally { setTesting(false); }
  };

  return (
    <div data-testid="welcome-message-card" className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`p-2 rounded-lg shrink-0 ${tpl.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-white/40"}`}>
            <PartyPopper size={18} />
          </div>
          <div className="min-w-0">
            <div className="text-white font-semibold">Welcome to AutoHire Message</div>
            <div className="text-white/50 text-xs mt-0.5 leading-snug">
              Automatically texts + emails every renter the moment their rental becomes <b className="text-white/70">Active</b> (agreement signed / vehicle picked up). Sends exactly once per booking and logs to the booking's communication history.
            </div>
          </div>
        </div>
        <Switch data-testid="welcome-message-switch" checked={tpl.enabled} disabled={saving}
          onCheckedChange={(v) => { setTpl({ ...tpl, enabled: v }); save({ enabled: v }); }}
          className="data-[state=checked]:bg-emerald-500 mt-0.5 shrink-0" />
      </div>

      <button data-testid="welcome-message-edit-toggle" onClick={() => setOpenEditor(!openEditor)}
        className="text-xs font-bold text-[#F2B705] hover:underline">
        {openEditor ? "▾ Hide template editor" : "▸ Edit message template"}
      </button>

      {openEditor && (
        <div className="space-y-3 border-t border-white/10 pt-3">
          <div>
            <label className="text-[11px] text-white/45">Email subject</label>
            <input data-testid="welcome-subject-input" value={tpl.email_subject}
              onChange={(e) => setTpl({ ...tpl, email_subject: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none focus:border-[#F2B705] mt-1" />
          </div>
          <div>
            <label className="text-[11px] text-white/45">Message body (used for both SMS and email)</label>
            <Textarea data-testid="welcome-body-input" rows={14} value={tpl.body}
              onChange={(e) => setTpl({ ...tpl, body: e.target.value })}
              className="bg-black border-white/15 text-white text-sm focus:border-[#F2B705] mt-1" />
          </div>
          <p className="text-white/40 text-[11px]">
            Placeholders filled in automatically: {(tpl.placeholders || []).map((p) => (
              <code key={p} className="bg-white/10 rounded px-1.5 py-0.5 mx-0.5 text-[#F2B705]">{p}</code>
            ))}
          </p>
          <div className="flex gap-2">
            <button data-testid="welcome-message-save" onClick={() => save({})} disabled={saving}
              className="flex items-center gap-1.5 bg-[#F2B705] text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-white transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save Template
            </button>
            <button data-testid="welcome-message-test-send" onClick={sendTest} disabled={testing}
              className="flex items-center gap-1.5 border border-emerald-400/40 text-emerald-300 text-xs font-bold px-4 py-2 rounded-md hover:bg-emerald-400/10 disabled:opacity-60">
              {testing ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Send Me a Test
            </button>
            <button data-testid="welcome-message-reset" onClick={() => {
              if (!window.confirm("Reset the welcome message to the standard AutoHire template?")) return;
              const next = { ...tpl, email_subject: tpl.default_subject, body: tpl.default_body };
              setTpl(next); save({ email_subject: next.email_subject, body: next.body });
            }} className="flex items-center gap-1.5 border border-white/15 text-white/60 text-xs font-bold px-4 py-2 rounded-md hover:bg-white/5">
              <RotateCcw size={13} /> Reset to Default
            </button>
          </div>
          {tpl.updated_at && <p className="text-white/30 text-[11px]">Last edited {fmtDateTime(tpl.updated_at)} by {tpl.updated_by}</p>}
        </div>
      )}
    </div>
  );
};

export default WelcomeMessageEditor;
